<template>
    <footer id="contact" class="welcome-footer" dir="rtl">
        <div class="welcome-footer__main mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div class="welcome-footer__grid">
                <div class="welcome-footer__brand-col">
                    <router-link :to="{ name: 'welcome' }" class="welcome-footer__brand" aria-label="البرمجة مع غنيم">
                        <img :src="logoWhiteSrc" alt="شعار البرمجة مع غنيم" class="welcome-footer__logo" />
                    </router-link>
                    <p v-if="footerDescription" class="welcome-footer__description">
                        {{ footerDescription }}
                    </p>
                    <div v-if="socialItems.length" class="welcome-footer__social">
                        <a
                            v-for="(item, index) in socialItems"
                            :key="item.brand + '-' + index"
                            :href="item.href"
                            :aria-label="item.label"
                            :title="item.label"
                            :target="capRunning ? '_system' : '_blank'"
                            rel="noopener noreferrer"
                            class="welcome-footer__social-link"
                            :style="{ '--social-brand-color': item.color }"
                        >
                            <welcome-social-brand-icon :brand="item.brand" :size="20" />
                        </a>
                    </div>
                </div>

                <div class="welcome-footer__links-col">
                    <h3 class="welcome-footer__heading">روابط سريعة</h3>
                    <ul class="welcome-footer__links">
                        <li v-for="link in navLinks" :key="link.label">
                            <router-link :to="{ name: link.route }" class="welcome-footer__link">
                                {{ link.label }}
                            </router-link>
                        </li>
                        <li>
                            <a
                                :href="allLinksUrl"
                                class="welcome-footer__link"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                كل الروابط
                            </a>
                        </li>
                    </ul>
                </div>

                <div class="welcome-footer__contact-col">
                    <h3 class="welcome-footer__heading">تواصل معنا</h3>
                    <ul class="welcome-footer__contact-list">
                        <li v-if="whatsappContact">
                            <a
                                :href="whatsappContact.href"
                                class="welcome-footer__contact-item welcome-footer__contact-item--whatsapp"
                                :target="capRunning ? '_system' : '_blank'"
                                rel="noopener noreferrer"
                            >
                                <span class="welcome-footer__contact-icon welcome-footer__contact-icon--whatsapp">
                                    <welcome-social-brand-icon brand="whatsapp" :size="18" />
                                </span>
                                <span class="welcome-footer__contact-text-wrap">
                                    <span class="welcome-footer__contact-label">WhatsApp</span>
                                    <span v-if="whatsappContact.displayPhone" class="welcome-footer__contact-text ltr-dir">
                                        {{ whatsappContact.displayPhone }}
                                    </span>
                                </span>
                            </a>
                        </li>
                        <li v-if="contactEmail">
                            <a :href="emailHref" class="welcome-footer__contact-item">
                                <span class="welcome-footer__contact-icon welcome-footer__contact-icon--email">
                                    <lucide-icon name="Mail" class="h-4 w-4" />
                                </span>
                                <span class="welcome-footer__contact-text-wrap">
                                    <span class="welcome-footer__contact-label">البريد الإلكتروني</span>
                                    <span class="welcome-footer__contact-text ltr-dir">{{ contactEmail }}</span>
                                </span>
                            </a>
                        </li>
                        <li v-if="showPhoneLink">
                            <a :href="phoneHref" class="welcome-footer__contact-item">
                                <span class="welcome-footer__contact-icon">
                                    <lucide-icon name="Phone" class="h-4 w-4" />
                                </span>
                                <span class="welcome-footer__contact-text-wrap">
                                    <span class="welcome-footer__contact-label">الهاتف</span>
                                    <span class="welcome-footer__contact-text ltr-dir">{{ contactPhone }}</span>
                                </span>
                            </a>
                        </li>
                        <li v-if="!whatsappContact && !contactPhone && !contactEmail" class="welcome-footer__contact-empty">
                            تواصل معنا عبر الدعم الفني أو حسابات التواصل الاجتماعي.
                        </li>
                    </ul>
                    <router-link :to="{ name: 'help' }" class="welcome-footer__support-btn">
                        <lucide-icon name="Headphones" class="h-4 w-4" />
                        <span>الدعم الفني</span>
                    </router-link>
                </div>
            </div>
        </div>

        <div class="welcome-footer__bar">
            <div class="welcome-footer__bar-inner mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div v-if="creditsHtml" class="welcome-footer__credits" v-html="creditsHtml" />
                <p v-else class="welcome-footer__copyright">
                    © {{ copyrightYear }} البرمجة مع غنيم. جميع الحقوق محفوظة.
                </p>
            </div>
        </div>
    </footer>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed';
import LucideIcon from './LucideIcon.vue';
import WelcomeSocialBrandIcon from './WelcomeSocialBrandIcon.vue';
import { ALL_LINKS_URL, LOGO_WHITE_SRC } from './landing-data';
import {
    FOOTER_NAV_LINKS,
    buildFooterSocialItems,
    buildWhatsappContact,
    buildWhatsappHref,
    getFooterCredits,
    getFooterDescription,
} from './landing-footer-data';

const DEFAULT_DESCRIPTION = 'منصة تعليمية لتعليم البرمجة والذكاء الاصطناعي لطلاب أولى وتانية ثانوي بطريقة مبسطة وعملية.';

export default {
    name: 'WelcomeFooter',
    components: { LucideIcon, WelcomeSocialBrandIcon },
    data() {
        return {
            navLinks: FOOTER_NAV_LINKS,
            logoWhiteSrc: LOGO_WHITE_SRC,
            allLinksUrl: ALL_LINKS_URL,
        };
    },
    computed: {
        ...GlobalComputed,
        welcomeData() {
            return this.$store.state.globalModule.welcomeData;
        },
        socialItems() {
            const items = buildFooterSocialItems(this.appData);
            const whatsappHref = buildWhatsappHref(this.appData);

            if (whatsappHref && this.appData && this.appData.whatsapp_url) {
                items.push({
                    brand: 'whatsapp',
                    label: 'WhatsApp',
                    color: '#25D366',
                    href: whatsappHref,
                });
            }

            return items;
        },
        whatsappContact() {
            return buildWhatsappContact(this.appData);
        },
        footerDescription() {
            return getFooterDescription(this.appData) || DEFAULT_DESCRIPTION;
        },
        contactPhone() {
            return this.appData ? this.appData.contact_phone : null;
        },
        contactEmail() {
            return this.appData ? this.appData.contact_email : null;
        },
        showPhoneLink() {
            return this.contactPhone && !this.whatsappContact;
        },
        phoneHref() {
            if (!this.contactPhone) {
                return null;
            }
            const digits = String(this.contactPhone).replace(/[^\d+]/g, '');
            return `tel:${digits}`;
        },
        emailHref() {
            return this.contactEmail ? `mailto:${this.contactEmail}` : null;
        },
        creditsHtml() {
            return getFooterCredits(this.appData, this.welcomeData);
        },
        copyrightYear() {
            return new Date().getFullYear();
        },
    },
};
</script>
